import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Pill, Plus, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import PageHeader from '@/components/PageHeader';
import { getMedications, createMedication, toggleMedication } from '@/utils/api';
import { toast } from 'sonner';

export default function GestioneFarmaci({ patient }) {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMed, setNewMed] = useState({
    nome: '',
    dosaggio: '',
    orari: ['08:00', '20:00'],
  });

  const loadMedications = async () => {
    try {
      const response = await getMedications(patient.id);
      setMedications(response.data);
    } catch (error) {
      console.error('Errore nel caricamento farmaci:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedications();
  }, [patient.id]);

  const handleAddMedication = async (e) => {
    e.preventDefault();

    try {
      await createMedication({
        patient_id: patient.id,
        ...newMed,
      });

      toast.success('Farmaco aggiunto con successo!');
      setDialogOpen(false);
      setNewMed({ nome: '', dosaggio: '', orari: ['08:00', '20:00'] });
      loadMedications();
    } catch (error) {
      console.error('Errore nell\'aggiunta del farmaco:', error);
      toast.error('Errore nell\'aggiunta del farmaco');
    }
  };

  const handleToggle = async (medId, currentState) => {
    try {
      await toggleMedication(medId);
      toast.success(currentState ? 'Farmaco disattivato' : 'Farmaco attivato');
      loadMedications();
    } catch (error) {
      console.error('Errore nel toggle farmaco:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  if (loading) {
    return (
      <div className="page-container pb-24">
        <PageHeader title="Gestione Farmaci" subtitle="Gestisci i tuoi farmaci" icon={Pill} />
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24">
      <PageHeader
        title="Gestione Farmaci"
        subtitle="Gestisci i tuoi farmaci"
        icon={Pill}
        action={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="btn-add-medication"
                className="min-h-[56px] px-6 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                <Plus className="w-5 h-5 mr-2" />
                Aggiungi
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-3xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Nuovo Farmaco</DialogTitle>
                <DialogDescription className="text-base">Aggiungi un nuovo farmaco alla tua terapia</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddMedication} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="nome" className="text-base font-medium mb-2 block">
                    Nome Farmaco
                  </Label>
                  <Input
                    id="nome"
                    data-testid="input-med-nome"
                    required
                    value={newMed.nome}
                    onChange={(e) => setNewMed({ ...newMed, nome: e.target.value })}
                    className="h-14 text-base rounded-xl"
                    placeholder="es. Levodopa"
                  />
                </div>
                <div>
                  <Label htmlFor="dosaggio" className="text-base font-medium mb-2 block">
                    Dosaggio
                  </Label>
                  <Input
                    id="dosaggio"
                    data-testid="input-med-dosaggio"
                    required
                    value={newMed.dosaggio}
                    onChange={(e) => setNewMed({ ...newMed, dosaggio: e.target.value })}
                    className="h-14 text-base rounded-xl"
                    placeholder="es. 100mg"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="btn-save-medication"
                  className="w-full min-h-[56px] text-base font-semibold rounded-xl bg-blue-600 hover:bg-blue-700"
                >
                  Salva Farmaco
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="space-y-4">
        {medications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-3xl p-12 soft-shadow border border-slate-200/60 text-center"
          >
            <Pill className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Nessun farmaco</h3>
            <p className="text-slate-600">Aggiungi il primo farmaco per iniziare</p>
          </motion.div>
        ) : (
          medications.map((med, idx) => (
            <motion.div
              key={med.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="bg-white rounded-3xl p-6 soft-shadow border border-slate-200/60"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${med.colore}20` }}
                    >
                      <Pill className="w-5 h-5" style={{ color: med.colore }} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{med.nome}</h3>
                      <p className="text-sm text-slate-600">{med.dosaggio}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <div className="flex gap-2 flex-wrap">
                      {med.orari.map((orario, i) => (
                        <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
                          {orario}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    data-testid={`switch-med-${idx}`}
                    checked={med.attivo}
                    onCheckedChange={() => handleToggle(med.id, med.attivo)}
                    className="data-[state=checked]:bg-green-600"
                  />
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
